import type { Underlying, UnderlyingAssetType } from 'models/underlying';
import type { UnderlyingCodeType } from 'models/underlying/base';
import type { CommodityUnderlying } from 'models/underlying/commodity';
import type { CreditUnderlying } from 'models/underlying/credit';
import type { EquityUnderlying } from 'models/underlying/equity';
import type { ForexUnderlying } from 'models/underlying/forex';
import type { RateUnderlying } from 'models/underlying/rate';

const generateEquityUnderlyingsGrid = (codeType: DefaultCodeType) => (underlyings: EquityUnderlying[]) =>
  mapUnderlyingsToCodeType(underlyings, codeType === 'Bloomberg' ? 'bloombergTickerCode' : 'reutersCode');

const generateCommodityUnderlyingsGrid = (codeType: DefaultCodeType) => (underlyings: CommodityUnderlying[]) =>
  mapUnderlyingsToCodeType(underlyings, codeType === 'Bloomberg' ? 'bloombergTickerCode' : 'reutersCode');

const generateCreditUnderlyingsGrid = (underlyings: CreditUnderlying[]) =>
  mapUnderlyingsToCodeType(underlyings, 'curveName');

const generateForexUnderlyingsGrid = (underlyings: ForexUnderlying[]) => mapUnderlyingsToCodeType(underlyings, 'name');

const generateRateUnderlyingsGrid = (codeType: DefaultCodeType) => (underlyings: RateUnderlying[]) =>
  mapUnderlyingsToCodeType(underlyings, codeType === 'Bloomberg' ? 'bloombergTickerCode' : 'reutersCode');

type DefaultCodeType = Extract<UnderlyingCodeType, 'Reuters' | 'Bloomberg'>;

function mapUnderlyingsToCodeType<T>(underlyings: T[], codeType: keyof T): string[][] {
  return underlyings.map(underlying => [(underlying[codeType] ?? '').toString()]);
}

export const generateAllUnderlyingsExcelGrid =
  (codeType: DefaultCodeType) => (underlyingsGroupedByAssetType: Map<UnderlyingAssetType, Underlying[]>) => {
    const commodityUnderlyings = (underlyingsGroupedByAssetType.get('Commodity') as CommodityUnderlying[]) ?? [];
    const creditUnderlyings = (underlyingsGroupedByAssetType.get('CreditRate') as CreditUnderlying[]) ?? [];
    const equityUnderlyings = (underlyingsGroupedByAssetType.get('Equity') as EquityUnderlying[]) ?? [];
    const forexUnderlyings = (underlyingsGroupedByAssetType.get('Forex') as ForexUnderlying[]) ?? [];
    const rateUnderlyings = (underlyingsGroupedByAssetType.get('Rate') as RateUnderlying[]) ?? [];

    return [
      ...generateCommodityUnderlyingsGrid(codeType)(commodityUnderlyings),
      ...generateCreditUnderlyingsGrid(creditUnderlyings),
      ...generateEquityUnderlyingsGrid(codeType)(equityUnderlyings),
      ...generateForexUnderlyingsGrid(forexUnderlyings),
      ...generateRateUnderlyingsGrid(codeType)(rateUnderlyings),
    ];
  };
